/**
 * Electrical-fixture KIND taxonomy (ELECTRICAL FIXTURES round, 2026-08 —
 * docs/MODELER.md "ELECTRICAL FIXTURES family"). The kind is the single source the
 * plan-symbol builder, the procedural 3D body, and the seeded firm defaults all
 * branch on — the objectKinds.ts pattern applied to wall-mounted small fixtures.
 *
 * Every fixture = an AUTHORED plan symbol (the drafting-standard electrical glyph —
 * duplex circle-with-ticks, S for switch, wall-light glyph — drawn at the wall face
 * on the fixture's own level ALWAYS, regardless of cut height: a 12" outlet sits
 * below every cut plane and must still print) + a simple procedural 3D BODY (cover
 * plates, sconce mass) that appears at true height in sections/interior elevations.
 * The symbol is the ONLY plan representation — no outline prism, no projection.
 *
 * Kinds are additive by design: a later kind (junction box, thermostat, smoke
 * detector…) = a value here + a symbol branch + a body branch + a seeded type —
 * never a schema reshape. Circuiting, panel schedules, electrical-plan view types,
 * and config marks for fixtures are explicitly OUT OF SCOPE until electrical plans
 * are designed (recorded in docs/MODELER.md).
 *
 * PLACEMENT FRAME convention (shared by symbols and bodies): the fixture anchors at
 * its own center point ON the mounted wall face — local x runs along the wall
 * centered on 0, local y extends off the face INTO the room, z is centered on the
 * mount height (code heights measure to device center). `mountHeightFt` is a
 * PLAIN INSTANCE param (never stored on the type) — a freshly created instance is
 * seeded from `FIXTURE_MOUNT_STANDARD_FT` below at creation time, then edited like
 * any ordinary value.
 */

import { lightStyleOf } from './lightStyles'

export const FIXTURE_KINDS = [
  // FAMILY CATEGORIES round (2026-08-26): `family` is the kind's DEFAULT family
  // (families.ts#familyCategoryOf) — see objectKinds.ts's identical note.
  { value: 'outlet', label: 'Duplex Outlet', family: 'Electrical Fixtures' },
  { value: 'switch', label: 'Switch', family: 'Electrical Fixtures' },
  { value: 'sconce', label: 'Sconce', family: 'Lighting Fixtures' },
  { value: 'lowVoltage', label: 'Low-Voltage Outlet', family: 'Electrical Fixtures' },
  // CEILING HOSTING round (2026-08-23, Matt: "Lights should be under electrical
  // fixtures, not objects... We already have sconces there."): moved here from
  // objectKinds.ts's OBJECT_KINDS — these three now HOST INTO A CEILING (see
  // CEILING_FIXTURE_KINDS below), reversing Round C's "freestanding, no hosting
  // work" call now that Matt has seen it live and wants a real host relationship,
  // same as sconce hosts into a wall.
  { value: 'recessedCan', label: 'Recessed Can Light', family: 'Lighting Fixtures' },
  { value: 'pendant', label: 'Pendant Light', family: 'Lighting Fixtures' },
  { value: 'flushMount', label: 'Flush-Mount Ceiling Light', family: 'Lighting Fixtures' },
  // FIT-OUT CATALOG (2026-09-30, Matt): electrical EQUIPMENT goes under Electrical
  { value: 'panel', label: 'Electrical Panel', family: 'Electrical Fixtures' },
  { value: 'subpanel', label: 'Subpanel', family: 'Electrical Fixtures' },
  { value: 'meter', label: 'Meter / Main', family: 'Electrical Fixtures' },
  { value: 'evCharger', label: 'EV Charger', family: 'Electrical Fixtures' },
  { value: 'inverter', label: 'Solar Inverter', family: 'Electrical Fixtures' },
  { value: 'disconnect', label: 'Disconnect', family: 'Electrical Fixtures' },
  { value: 'thermostat', label: 'Thermostat', family: 'Electrical Fixtures' },
  // FIT-OUT CATALOG: BATH ACCESSORIES — a new family, all wall-mounted (they ride
  // their wall like an outlet does), sized per type
  { value: 'grabBar', label: 'Grab Bar', family: 'Bath Accessories' },
  { value: 'towelBar', label: 'Towel Bar', family: 'Bath Accessories' },
  { value: 'towelRing', label: 'Towel Ring', family: 'Bath Accessories' },
  { value: 'robeHook', label: 'Robe Hook', family: 'Bath Accessories' },
  { value: 'toiletPaperHolder', label: 'Toilet Paper Holder', family: 'Bath Accessories' },
  { value: 'mirror', label: 'Mirror', family: 'Bath Accessories' },
  { value: 'medicineCabinet', label: 'Medicine Cabinet', family: 'Bath Accessories' },
  // LIGHT STYLES (2026-10-07): the rest of the generic light TYPES — each carries a list of
  // 3D styles chosen per instance (lightStyles.ts)
  { value: 'chandelier', label: 'Chandelier', family: 'Lighting Fixtures' },
  { value: 'stepLight', label: 'Step Light', family: 'Lighting Fixtures' },
  { value: 'postLantern', label: 'Post / Pier Lantern', family: 'Lighting Fixtures' },
  { value: 'pathLight', label: 'Path Light', family: 'Lighting Fixtures' },
  // CEILING DEVICES (docs/bim-plan/28 round 3, the RCP package): smoke / CO detectors and HVAC
  // supply / return registers — ceiling-hosted like a recessed can, coded on the reflected
  // ceiling plan (SD, CO, SR, RR) and counted in the lighting schedule's device legend.
  // Revit's own family names: Fire Alarm Devices, Air Terminals.
  { value: 'smokeDetector', label: 'Smoke Detector', family: 'Fire Alarm Devices' },
  { value: 'coDetector', label: 'CO Detector', family: 'Fire Alarm Devices' },
  { value: 'supplyRegister', label: 'Supply Register', family: 'Air Terminals' },
  { value: 'returnRegister', label: 'Return Register', family: 'Air Terminals' },
] as const

/** CEILING DEVICES: each device kind's code on the RCP and in its legend */
export const CEILING_DEVICE_CODES: Record<string, string> = {
  smokeDetector: 'SD', coDetector: 'CO', supplyRegister: 'SR', returnRegister: 'RR',
}
export const CEILING_DEVICE_KINDS: readonly string[] = Object.keys(CEILING_DEVICE_CODES)

/** Round C (docs/bim-plan/18 §6): fixture kinds that EMIT LIGHT — the sconce plus
 *  the three ceiling kinds moved in from objects (2026-08-23). Toggleable via a
 *  paired switch at walkthrough time; see lighting.ts. */
export const FIXTURE_LIGHT_KINDS: readonly string[] = [
  'sconce', 'recessedCan', 'pendant', 'flushMount',
  // LIGHT STYLES (2026-10-07)
  'chandelier', 'stepLight', 'postLantern', 'pathLight',
]

/** Kinds mounted on a WALL FACE — the station-along-path model (hostOffsetFt +
 *  sideFlip + mountHeightFt) applies to these alone. */
export const WALL_FIXTURE_KINDS: readonly string[] = [
  'outlet', 'switch', 'sconce', 'lowVoltage',
  // FIT-OUT CATALOG (2026-09-30)
  'panel', 'subpanel', 'meter', 'evCharger', 'inverter', 'disconnect', 'thermostat',
  'grabBar', 'towelBar', 'towelRing', 'robeHook', 'toiletPaperHolder', 'mirror', 'medicineCabinet',
  // LIGHT STYLES (2026-10-07): a step light rides its wall like any other wall kind
  'stepLight',
]

/** FIT-OUT CATALOG: wall kinds whose body is SIZED PER TYPE (widthFt/heightFt/depthFt
 *  type params, a 24" vs 36" grab bar) rather than one fixed device size — the
 *  FIXTURE_BODY_DIMS entry is only the fallback for a type that sets nothing. */
export const SIZED_WALL_FIXTURE_KINDS: readonly string[] = [
  'panel', 'subpanel', 'meter', 'evCharger', 'inverter', 'disconnect', 'thermostat',
  'grabBar', 'towelBar', 'towelRing', 'robeHook', 'toiletPaperHolder', 'mirror', 'medicineCabinet',
]

/**
 * CEILING HOSTING round (2026-08-23): kinds mounted ON/IN A CEILING. A ceiling is
 * a 2D area, not a path — the wall fixture's 1D "station along a path" model
 * doesn't fit, so these use the OBJECT anchor model instead (`path` — a single-
 * node 2D point — + `rotationDeg`, schema.ts), just with a real `hostId` pointing
 * at the specific ceiling element underneath (cascade-delete, "moves with its
 * ceiling" — `deleteElement`/`hostedElements` in doc.ts are already category-
 * generic, so this falls out for free). Height is DERIVED from the host
 * ceiling's own resolved z (never a stored/seeded guess) — see
 * `fixtureSolid`/`fixtureZSpan` in generate3d.ts. `widthFt`/`depthFt`/`heightFt`
 * (+ `trimShape` for recessedCan) are TYPE params for these kinds, the
 * objectKinds.ts convention carried over verbatim (curated size/shape seeds).
 */
export const CEILING_FIXTURE_KINDS: readonly string[] = ['recessedCan', 'pendant', 'flushMount', 'chandelier',
  // CEILING DEVICES (bim-plan/28 round 3)
  'smokeDetector', 'coDetector', 'supplyRegister', 'returnRegister']

export function isCeilingFixtureKind(kind: string | undefined): boolean {
  return CEILING_FIXTURE_KINDS.includes(String(kind ?? ''))
}

/**
 * LIGHT STYLES (2026-10-07): kinds that stand on the GROUND (or on a pier) — post / pier
 * lanterns and path lights. They use the ceiling kinds' point-anchor model (`path` +
 * `rotationDeg`) but host into NOTHING: their base sits at the level plus the instance's
 * Base Height (`baseOffsetFt` — a pier top, a wall cap). Placed anywhere, like an object.
 */
export const GROUND_FIXTURE_KINDS: readonly string[] = ['postLantern', 'pathLight']

export function isGroundFixtureKind(kind: string | undefined): boolean {
  return GROUND_FIXTURE_KINDS.includes(String(kind ?? ''))
}

/** every kind placed by a single anchor POINT (ceiling + ground) — the object anchor model
 *  (`path` + `rotationDeg`), never a wall station */
export const POINT_FIXTURE_KINDS: readonly string[] = [...CEILING_FIXTURE_KINDS, ...GROUND_FIXTURE_KINDS]

export function isPointFixtureKind(kind: string | undefined): boolean {
  return POINT_FIXTURE_KINDS.includes(String(kind ?? ''))
}

/**
 * Fallback mount height for a ceiling-kind fixture with NO host ceiling —
 * migration graceful-degrade only (2026-08-24 hardening: lights migrate to the
 * fixture category even in a model with no ceilings drawn yet, and must still
 * render somewhere sane until a ceiling exists to adopt them — the re-adopt
 * pass in migrateCeilingLightsToFixture attaches them the moment one does).
 * NEW placement always requires a real ceiling; this never applies there.
 */
export const UNHOSTED_CEILING_LIGHT_FT = 8

/**
 * RECESSED CAN RECESS (2026-10-12, Matt: "recessed lights don't actually cut themselves into
 * the ceiling and instead just show overlapping it … especially apparent when the lights are
 * turned on"): the can's look from below, shared by the 3D body (fixture/generate3d.ts) and the
 * lit lamp (lighting.ts#lightVisualOf) so the glow always sits ON the lamp, never on the ceiling
 * plane. Local z = 0 is the ceiling underside; the ceiling's aperture hole is the trim radius r.
 *  - the trim FLANGE: a ring just proud of the ceiling, lapping past the hole's edge
 *  - the BAFFLE: an open tube lining the hole from the ceiling up to the lamp
 *  - the LAMP (lens) set back up inside, `lensZ` above the ceiling
 */
export function recessedCanRecess(radiusFt: number): {
  flangeOuterFt: number; flangeDropFt: number; baffleInnerFt: number; lensZ: number
} {
  const r = Math.max(0.02, radiusFt)
  return {
    flangeOuterFt: r * 1.18,
    flangeDropFt: 0.012,
    baffleInnerFt: r * 0.86,
    // a 2" can sets its lamp back ~1", a 6" can ~1 3/4"
    lensZ: Math.min(0.15, Math.max(0.07, r * 0.6)),
  }
}

/** Round C follow-up (Matt: "ceiling hosted round and square recessed lights in
 *  2, 4, and 6 inch sizes"): the recessed can's TRIM shape — drives both the plan
 *  glyph and the 3D housing profile. Size itself rides the ordinary widthFt/
 *  depthFt (a curated 2"/4"/6" × round/square matrix, seed.ts), not a new param —
 *  a real housing has no size choice independent of its physical trim diameter.
 *  Moved here from objectKinds.ts (2026-08-23) alongside the kind itself. */
export const RECESSED_TRIM_SHAPES = [
  { value: 'round', label: 'Round' },
  { value: 'square', label: 'Square' },
] as const

export type FixtureKind = (typeof FIXTURE_KINDS)[number]['value']

export function fixtureKindLabel(kind: string | undefined): string {
  return FIXTURE_KINDS.find((k) => k.value === kind)?.label || 'Fixture'
}

/**
 * The kind's code-height MOUNT STANDARD in feet, to device CENTER — a firm-wide
 * constant (edited here in code, not per-type), used ONLY to seed a freshly
 * created instance's `mountHeightFt` (commands/elements.ts#seededFixtureMountHeight,
 * the door/window `sizeSeries[0]` precedent). Never stored on the type.
 */
export const FIXTURE_MOUNT_STANDARD_FT: Record<string, number> = {
  outlet: 1,        // 12" to center — receptacle code height
  switch: 4,        // 48" to center
  sconce: 5.5,      // 66" to center
  lowVoltage: 1,    // rides the receptacle height
  // FIT-OUT CATALOG (2026-09-30) — to CENTER, typical residential practice
  panel: 5.25, subpanel: 5.5, meter: 5, evCharger: 4, inverter: 5, disconnect: 5,
  thermostat: 4.75,              // 57" — the comfort-control height
  grabBar: 2 + 10 / 12,          // 34" — within the 33–36" grab-bar range
  towelBar: 4, towelRing: 4,     // 48"
  robeHook: 5.5,                 // 66"
  toiletPaperHolder: 2 + 2 / 12, // 26"
  mirror: 4 + 10 / 12,           // bottom ≈ 40" for a 36"-tall mirror
  medicineCabinet: 4 + 9 / 12,   // bottom ≈ 44" for a 26"-tall cabinet
  stepLight: 1.25,               // 15" — a low wall / stair-side step light
}

/**
 * The kind's 3D BODY dimensions in feet (width along the wall × height × depth off
 * the face) — one source for the procedural prototype, the snap/pick footprint, and
 * the z-extent. Outlet/switch/low-voltage are standard cover plates; the sconce is
 * an honest simple mass (backplate + lamp), per the procedural-tier doctrine.
 */
export const FIXTURE_BODY_DIMS: Record<string, { widthFt: number; heightFt: number; depthFt: number }> = {
  outlet: { widthFt: 2.75 / 12, heightFt: 4.5 / 12, depthFt: 0.6 / 12 },
  switch: { widthFt: 2.75 / 12, heightFt: 4.5 / 12, depthFt: 0.6 / 12 },
  sconce: { widthFt: 0.5, heightFt: 0.85, depthFt: 0.35 },
  lowVoltage: { widthFt: 2.75 / 12, heightFt: 4.5 / 12, depthFt: 0.6 / 12 },
  // FIT-OUT CATALOG fallbacks (a seeded type always carries its own size)
  panel: { widthFt: 14.5 / 12, heightFt: 30 / 12, depthFt: 4 / 12 },
  subpanel: { widthFt: 14.5 / 12, heightFt: 24 / 12, depthFt: 4 / 12 },
  meter: { widthFt: 11 / 12, heightFt: 14 / 12, depthFt: 4 / 12 },
  evCharger: { widthFt: 9 / 12, heightFt: 14 / 12, depthFt: 5 / 12 },
  inverter: { widthFt: 15 / 12, heightFt: 22 / 12, depthFt: 7 / 12 },
  disconnect: { widthFt: 8 / 12, heightFt: 10 / 12, depthFt: 4 / 12 },
  thermostat: { widthFt: 4 / 12, heightFt: 4 / 12, depthFt: 1 / 12 },
  grabBar: { widthFt: 3, heightFt: 3 / 12, depthFt: 3 / 12 },
  towelBar: { widthFt: 2, heightFt: 3 / 12, depthFt: 3 / 12 },
  towelRing: { widthFt: 7 / 12, heightFt: 8 / 12, depthFt: 3 / 12 },
  robeHook: { widthFt: 2 / 12, heightFt: 3 / 12, depthFt: 3 / 12 },
  toiletPaperHolder: { widthFt: 7 / 12, heightFt: 3 / 12, depthFt: 4 / 12 },
  mirror: { widthFt: 2.5, heightFt: 3, depthFt: 1 / 12 },
  medicineCabinet: { widthFt: 16 / 12, heightFt: 26 / 12, depthFt: 4 / 12 },
  stepLight: { widthFt: 6.6 / 12, heightFt: 3.2 / 12, depthFt: 1.3 / 12 },
}

/**
 * GANGED SWITCHES (2026-10-14, Matt: "Add the ability to gang more switches which I believe
 * grows the box … some more modern configurations can use less wall space for more
 * switches"): a switch fixture is one BOX of `gangs` side-by-side device positions, each
 * holding a `switchDevice` — one standard toggle, a stacked 2-in-1 or 3-in-1 device, or a
 * scene keypad (one gang running any number of lights or scenes). Each added gang widens
 * the cover plate ~1 13/16" and the box ~1 3/4", which is what the stud-conflict check and
 * the plan's box outline measure. Instance params (this switch's box, not its type).
 */
export const SWITCH_DEVICES = [
  { value: 'standard', label: 'Standard (1 per gang)', perGang: 1 },
  { value: 'stack2', label: 'Stacked 2-in-1', perGang: 2 },
  { value: 'stack3', label: 'Stacked 3-in-1', perGang: 3 },
  { value: 'keypad', label: 'Scene keypad', perGang: 0 },
] as const
export const MAX_SWITCH_GANGS = 6
/** the plate's gang-to-gang spacing (standard wallplates: 2 3/4", 4 9/16", 6 3/8" …) */
export const GANG_PITCH_FT = 1.8125 / 12

export function switchGangs(p: Record<string, unknown> | undefined): number {
  const g = Math.round(Number(p?.gangs))
  return Number.isFinite(g) ? Math.max(1, Math.min(MAX_SWITCH_GANGS, g)) : 1
}

export function switchDeviceOf(p: Record<string, unknown> | undefined): (typeof SWITCH_DEVICES)[number] {
  return SWITCH_DEVICES.find((d) => d.value === p?.switchDevice) ?? SWITCH_DEVICES[0]
}

/** the gang centers along the wall, feet, centered on 0 */
export function gangCentersFt(gangs: number): number[] {
  return Array.from({ length: gangs }, (_, i) => (i - (gangs - 1) / 2) * GANG_PITCH_FT)
}

/** the wall kinds that sit in an electrical BOX inside the wall */
export const BOXED_WALL_FIXTURE_KINDS: readonly string[] = ['outlet', 'switch', 'lowVoltage']

/**
 * The device box inside the wall behind a boxed fixture (switch / outlet / low-voltage):
 * its outer width along the wall and its depth back from the wall face. `p` = merged type
 * + instance params (a switch's gang count is per instance). Null for unboxed kinds.
 */
export function deviceBoxFt(kind: string | undefined, p?: Record<string, unknown>): { widthFt: number; depthFt: number } | null {
  if (!BOXED_WALL_FIXTURE_KINDS.includes(String(kind ?? ''))) return null
  const g = kind === 'switch' ? switchGangs(p) : 1
  // a 1-gang new-work box is ~2 1/4" outside; each gang adds ~1 3/4". Low-voltage is a
  // mud ring, but it still wants the same clear bay.
  return { widthFt: (2.25 + 1.75 * (g - 1)) / 12, depthFt: 3 / 12 }
}

/** body size — a SIZED wall kind (SIZED_WALL_FIXTURE_KINDS) reads its type's own
 *  widthFt/heightFt/depthFt when set; a STYLED wall light (sconce, step light) is its
 *  style's own size (LIGHT STYLES, 2026-10-07 — pass the merged type + instance params so
 *  the instance's style is seen); every other wall kind is the fixed device size */
export function fixtureBodyDims(
  kind: string | undefined,
  typeParams?: Record<string, unknown>,
): { widthFt: number; heightFt: number; depthFt: number } {
  const style = (kind === 'sconce' || kind === 'stepLight') ? lightStyleOf(kind, typeParams) : null
  if (style) return { widthFt: style.w / 12, heightFt: (style.z1 - style.z0) / 12, depthFt: (style.y1 - style.y0) / 12 }
  const base = FIXTURE_BODY_DIMS[kind || ''] ?? FIXTURE_BODY_DIMS.outlet
  // GANGED SWITCHES: the cover plate grows a gang pitch per added gang
  if (kind === 'switch') {
    const g = switchGangs(typeParams)
    return g > 1 ? { ...base, widthFt: base.widthFt + GANG_PITCH_FT * (g - 1) } : base
  }
  if (!typeParams || !SIZED_WALL_FIXTURE_KINDS.includes(String(kind ?? ''))) return base
  const n = (v: unknown, d: number) => (typeof v === 'number' && v > 0 ? v : d)
  return {
    widthFt: n(typeParams.widthFt, base.widthFt),
    heightFt: n(typeParams.heightFt, base.heightFt),
    depthFt: n(typeParams.depthFt, base.depthFt),
  }
}

/**
 * The mount-height resolver every consumer reads through (generator, pick config,
 * vertical grip; the plan symbol never needs it): the plain instance value, or the
 * kind's code-table default for a synthetic caller that has no instance yet (a
 * bare type-diagram preview, a not-yet-created ghost).
 */
export function fixtureMountHeightFt(
  params: Record<string, unknown>,
  typeParams: Record<string, unknown> | undefined,
): number {
  const inst = params.mountHeightFt
  if (typeof inst === 'number' && isFinite(inst)) return inst
  const kind = String(typeParams?.fixtureKind ?? params.fixtureKind ?? 'outlet')
  return FIXTURE_MOUNT_STANDARD_FT[kind] ?? 1
}
